import { grpcClientFactory } from './grpcClientFactory';
import { buildConsentWordingVersions } from '../constants/consent';
import { parseAvatar } from '../utils/avatarProfile';
import '../../_proto/landscope-map-viewer_pb';
// Type-safe imports to avoid TypeScript definition issues  
const protoModule = require('../../_proto/landscope-map-viewer_pb');
class AuthService {
    constructor() {
        this.loggedInUserClient = null;
        this.SESSION_ID_KEY = 'landscope_session_id';
        this.SESSION_TOKEN_KEY = 'landscope_session_token';
        this.userClient = grpcClientFactory.createUserClient({ enableCompression: true });
        // Don't create authenticated client in constructor - use lazy initialization
    }
    /**
     * Start external login (e.g., Google) and return provider auth URL. A partner
     * referral code is sent as a code only; the server adds it to the URL the
     * provider returns to.
     */
    async startExternalLogin(provider = 'GOOGLE', referralCode = '') {
        return new Promise((resolve, reject) => {
            var _a, _b, _c, _d, _e, _f, _g, _h;
            try {
                const Req = (_c = (_b = (_a = proto === null || proto === void 0 ? void 0 : proto.landscope) === null || _a === void 0 ? void 0 : _a.mapviewer) === null || _b === void 0 ? void 0 : _b.v1) === null || _c === void 0 ? void 0 : _c.StartExternalLoginRequest;
                const ProviderEnum = (_h = (_g = (_f = (_e = (_d = proto === null || proto === void 0 ? void 0 : proto.landscope) === null || _d === void 0 ? void 0 : _d.mapviewer) === null || _e === void 0 ? void 0 : _e.v1) === null || _f === void 0 ? void 0 : _f.ExternalProvider) === null || _g === void 0 ? void 0 : _g.EXTERNAL_PROVIDER_GOOGLE) !== null && _h !== void 0 ? _h : 1;
                if (!Req) {
                    reject(new Error('StartExternalLoginRequest type not found. Regenerate grpc-web protos.'));
                    return;
                }
                const req = new Req();
                req.setProvider(ProviderEnum);
                if (referralCode) {
                    req.setReferralCode(referralCode);
                }
                this.userClient.startExternalLogin(req, {}, (err, resp) => {
                    var _a;
                    if (err || !resp) {
                        reject(err || new Error('no response'));
                        return;
                    }
                    const url = (_a = resp.getAuthUrl) === null || _a === void 0 ? void 0 : _a.call(resp);
                    if (!url) {
                        reject(new Error('no auth url'));
                        return;
                    }
                    resolve(url);
                });
            }
            catch (e) {
                reject(e);
            }
        });
    }
    /**
     * Complete external login using idpIntentId and idpIntentToken; stores nothing itself
     */
    async completeExternalLogin(intentId, intentToken, referralCode = '') {
        return new Promise((resolve, reject) => {
            var _a, _b, _c;
            try {
                const Req = (_c = (_b = (_a = proto === null || proto === void 0 ? void 0 : proto.landscope) === null || _a === void 0 ? void 0 : _a.mapviewer) === null || _b === void 0 ? void 0 : _b.v1) === null || _c === void 0 ? void 0 : _c.CompleteExternalLoginRequest;
                if (!Req) {
                    reject(new Error('CompleteExternalLoginRequest type not found. Regenerate grpc-web protos.'));
                    return;
                }
                const req = new Req();
                req.setIntentId(intentId);
                req.setIntentToken(intentToken);
                if (referralCode) {
                    req.setReferralCode(referralCode);
                }
                // Use a fresh client with compression disabled for this one call to avoid any proxy quirks
                const localClient = grpcClientFactory.createUserClient({ enableCompression: false });
                localClient.completeExternalLogin(req, {}, (err, resp) => {
                    var _a, _b, _c, _d;
                    if (err) {
                        console.error('Complete external login error:', err);
                        reject(err);
                        return;
                    }
                    if (!resp) {
                        reject(new Error('no response'));
                        return;
                    }
                    const success = (_a = resp.getSuccess) === null || _a === void 0 ? void 0 : _a.call(resp);
                    const message = (_b = resp.getMessage) === null || _b === void 0 ? void 0 : _b.call(resp);
                    if (!success) {
                        // External login failed - check if it's a domain restriction
                        if (message && message.includes('Email domain not authorized')) {
                            reject(new Error('EMAIL_DOMAIN_NOT_ALLOWED'));
                            return;
                        }
                        reject(new Error(message || 'External login failed'));
                        return;
                    }
                    const sessionId = (_c = resp.getSessionId) === null || _c === void 0 ? void 0 : _c.call(resp);
                    const sessionToken = (_d = resp.getSessionToken) === null || _d === void 0 ? void 0 : _d.call(resp);
                    if (!sessionId || !sessionToken) {
                        reject(new Error('missing tokens'));
                        return;
                    }
                    resolve({ sessionId, sessionToken });
                });
            }
            catch (e) {
                reject(e);
            }
        });
    }
    /**
     * Returns valid session token or null
     */
    getToken() {
        return localStorage.getItem(this.SESSION_TOKEN_KEY);
    }
    /**
     * Returns session ID if available
     */
    getSessionId() {
        return localStorage.getItem(this.SESSION_ID_KEY);
    }
    /**
     * Check if user is authenticated (has valid tokens in localStorage)
     */
    isAuthenticated() {
        return !!(this.getToken() && this.getSessionId());
    }
    /**
     * Login with email and password, store tokens on success
     */
    async login(credentials) {
        return new Promise((resolve, reject) => {
            const LoginRequest = protoModule.LoginRequest;
            const request = new LoginRequest();
            request.setEmail(credentials.email);
            request.setPassword(credentials.password);
            this.userClient.login(request, {}, (err, response) => {
                if (err) {
                    console.error('Login error:', err);
                    reject(new Error(`Login failed: ${err.code} ${err.message}`));
                    return;
                }
                if (response) {
                    const success = response.getSuccess();
                    const message = response.getMessage();
                    if (success) {
                        const sessionId = response.getSessionId();
                        const sessionToken = response.getSessionToken();
                        if (sessionId && sessionToken) {
                            // Store tokens
                            localStorage.setItem(this.SESSION_ID_KEY, sessionId);
                            localStorage.setItem(this.SESSION_TOKEN_KEY, sessionToken);
                            resolve({ success: true, message: message || 'Login successful' });
                        }
                        else {
                            resolve({ success: false, message: 'Login succeeded but no tokens received' });
                        }
                    }
                    else {
                        resolve({ success: false, message: message || 'Login failed' });
                    }
                }
                else {
                    reject(new Error('No response received'));
                }
            });
        });
    }
    /**
     * Verify email with userId, code, and password, store tokens on success
     */
    async verify(credentials) {
        return new Promise((resolve, reject) => {
            const VerifyUserRequest = protoModule.VerifyUserRequest;
            const request = new VerifyUserRequest();
            request.setUserId(credentials.userId);
            request.setCode(credentials.code);
            request.setPassword(credentials.password);
            if (credentials.csrfToken) {
                request.setCsrfToken(credentials.csrfToken);
            }
            this.userClient.verify(request, {}, (err, response) => {
                if (err) {
                    reject(new Error(`Verification failed: ${err.code} ${err.message}`));
                    return;
                }
                if (response) {
                    const success = response.getSuccess();
                    const message = response.getMessage();
                    if (success) {
                        const sessionId = response.getSessionId();
                        const sessionToken = response.getSessionToken();
                        if (sessionId && sessionToken) {
                            // Store tokens
                            localStorage.setItem(this.SESSION_ID_KEY, sessionId);
                            localStorage.setItem(this.SESSION_TOKEN_KEY, sessionToken);
                            resolve({ success: true, message: message || 'Verification successful' });
                        }
                        else {
                            resolve({ success: false, message: 'Verification succeeded but no tokens received' });
                        }
                    }
                    else {
                        resolve({ success: false, message: message || 'Verification failed' });
                    }
                }
                else {
                    reject(new Error('No response received'));
                }
            });
        });
    }
    /**
     * Logout and clear stored tokens
     */
    async logout() {
        const sessionId = this.getSessionId();
        // If we have a session ID, try to call the logout RPC
        if (sessionId) {
            try {
                await this.callLogoutRPC(sessionId);
            }
            catch (error) {
                console.warn('Logout RPC failed, but clearing tokens anyway:', error);
                // Continue to clear tokens even if RPC fails
            }
        }
        // Always clear local tokens regardless of RPC success/failure
        localStorage.removeItem(this.SESSION_ID_KEY);
        localStorage.removeItem(this.SESSION_TOKEN_KEY);
        // Clear cached authenticated client
        this.loggedInUserClient = null;
    }
    /**
     * Call the logout RPC to invalidate session server-side
     */
    async callLogoutRPC(sessionId) {
        return new Promise((resolve, reject) => {
            const LogoutRequest = protoModule.LogoutRequest;
            const request = new LogoutRequest();
            request.setSessionId(sessionId);
            // Create authenticated client for this request
            const authenticatedClient = this.getAuthenticatedClient();
            const sessionToken = this.getToken();
            // Create metadata with authorization header
            const metadata = {
                'authorization': `Bearer ${sessionId}|${sessionToken}`
            };
            authenticatedClient.logout(request, metadata, (err, response) => {
                if (err) {
                    reject(new Error(`Logout RPC failed: ${err.code} ${err.message}`));
                    return;
                }
                if (response) {
                    const success = response.getSuccess();
                    const message = response.getMessage();
                    if (success) {
                        resolve();
                    }
                    else {
                        reject(new Error(message || 'Logout failed'));
                    }
                }
                else {
                    reject(new Error('No response received from logout RPC'));
                }
            });
        });
    }
    /**
     * Check authentication status with server using GetCurrentUser
     */
    async checkAuthStatus() {
        const sessionId = this.getSessionId();
        const sessionToken = this.getToken();
        if (!sessionId || !sessionToken) {
            return false;
        }
        try {
            await this.getCurrentUser();
            return true;
        }
        catch (error) {
            console.warn('AuthService: Session validation failed:', error);
            // Only clear tokens for specific error codes that indicate the session is definitively invalid
            // Don't clear for network errors or temporary server issues
            if (error.code === 16 || error.code === 7) { // UNAUTHENTICATED or PERMISSION_DENIED
                this.logout();
                return false;
            }
            else {
                // Return false but don't clear tokens for other error types
                return false;
            }
        }
    }
    /**
     * Get or create a LoggedInUserServiceClient with authentication headers
     */
    getAuthenticatedClient() {
        if (!this.loggedInUserClient) {
            this.loggedInUserClient = grpcClientFactory.createLoggedInUserClient({ enableCompression: true });
        }
        return this.loggedInUserClient;
    }
    /**
     * Get current user data from server
     */
    async getCurrentUser() {
        const sessionId = this.getSessionId();
        const sessionToken = this.getToken();
        if (!sessionId || !sessionToken) {
            throw new Error('No authentication tokens available');
        }
        return new Promise((resolve, reject) => {
            const GetCurrentUserRequest = protoModule.GetCurrentUserRequest;
            const request = new GetCurrentUserRequest();
            request.setSessionId(sessionId);
            // Create authenticated client for this request
            const authenticatedClient = this.getAuthenticatedClient();
            // Create metadata with authorization header manually
            const metadata = {
                'authorization': `Bearer ${sessionId}|${sessionToken}`
            };
            authenticatedClient.getCurrentUser(request, metadata, (err, response) => {
                var _a;
                if (err) {
                    console.error('AuthService: getCurrentUser error details:', {
                        code: err.code,
                        message: err.message,
                        details: err.details,
                        metadata: err.metadata
                    });
                    reject(new Error(`Failed to get current user: ${err.code} ${err.message}`));
                    return;
                }
                if (response) {
                    // Convert protobuf LoginMethod enum to string
                    const loginMethodEnum = response.getLoginMethod();
                    let loginMethod = 'unspecified';
                    if (loginMethodEnum === 1) { // LOGIN_METHOD_PASSWORD
                        loginMethod = 'password';
                    }
                    else if (loginMethodEnum === 2) { // LOGIN_METHOD_GOOGLE
                        loginMethod = 'google';
                    }
                    else if (loginMethodEnum === 3) { // LOGIN_METHOD_UNVERIFIED
                        loginMethod = 'unverified';
                    }
                    const userData = {
                        firstName: response.getFirstName(),
                        lastName: response.getLastName(),
                        email: response.getEmail(),
                        loginMethod: loginMethod,
                        userId: response.getUserId() || undefined,
                        commentColor: response.getCommentColor() || undefined,
                        avatarUrl: response.getAvatarUrl() || undefined,
                        needsUsagePurpose: response.getNeedsUsagePurpose() || false,
                        needsConsentChoice: response.getNeedsConsentChoice(),
                        avatar: parseAvatar(response.getAvatar()),
                        usagePurpose: response.getUsagePurpose(),
                        teamSize: response.getTeamSize(),
                    };
                    const activeWorkspace = (_a = response.getActiveWorkspace) === null || _a === void 0 ? void 0 : _a.call(response);
                    if (activeWorkspace) {
                        userData.activeWorkspace = {
                            id: activeWorkspace.getId(),
                            name: activeWorkspace.getName(),
                            role: activeWorkspace.getRole(),
                        };
                    }
                    const accountDeletion = response.getAccountDeletion();
                    if (accountDeletion) {
                        userData.accountDeletion = {
                            consequences: accountDeletion.getConsequencesList(),
                            reasonMaxLength: accountDeletion.getReasonMaxLength(),
                        };
                    }
                    resolve(userData);
                }
                else {
                    reject(new Error('No response received'));
                }
            });
        });
    }
    /**
     * Get both tokens for manual use
     */
    getTokens() {
        const sessionId = this.getSessionId();
        const sessionToken = this.getToken();
        if (sessionId && sessionToken) {
            return { sessionId, sessionToken };
        }
        return null;
    }
    /**
     * Store tokens directly (for when tokens are obtained outside of login flow)
     */
    storeTokens(sessionId, sessionToken) {
        localStorage.setItem(this.SESSION_ID_KEY, sessionId);
        localStorage.setItem(this.SESSION_TOKEN_KEY, sessionToken);
        // Clear cached authenticated client so it gets recreated with new tokens
        this.loggedInUserClient = null;
    }
    /**
     * Clear cached authenticated client (called when tokens are invalidated)
     */
    clearAuthenticatedClient() {
        this.loggedInUserClient = null;
    }
    /**
     * Request password reset (authenticated - uses email from session)
     */
    async requestPasswordReset() {
        return new Promise((resolve, reject) => {
            const request = new proto.google.protobuf.Empty();
            this.getAuthenticatedClient().requestPasswordReset(request, {}, (err, response) => {
                if (err) {
                    console.error('Request password reset error:', err);
                    reject(new Error(`Request failed: ${err.code} ${err.message}`));
                    return;
                }
                if (response) {
                    const success = response.getSuccess();
                    const message = response.getMessage();
                    resolve({ success, message: message || 'Reset request processed' });
                }
                else {
                    reject(new Error('No response received'));
                }
            });
        });
    }
    /**
     * Update editable profile preferences. Currently the comment marker color.
     * Pass an empty string to clear back to the default deterministic color.
     * Returns the stored value after server-side validation.
     */
    async updateProfile(commentColor) {
        return new Promise((resolve, reject) => {
            const UpdateProfileRequest = protoModule.UpdateProfileRequest;
            const request = new UpdateProfileRequest();
            request.setCommentColor(commentColor);
            this.getAuthenticatedClient().updateProfile(request, {}, (err, response) => {
                if (err) {
                    console.error('Update profile error:', err);
                    reject(new Error(`Request failed: ${err.code} ${err.message}`));
                    return;
                }
                if (response) {
                    resolve({ commentColor: response.getCommentColor() || '' });
                }
                else {
                    reject(new Error('No response received'));
                }
            });
        });
    }
    /**
     * Submit a support message or feature request. The message is emailed to the
     * team's internal inbox with the sender's identity attached server-side, so
     * no email client is involved. category selects the subject line the team sees.
     */
    async submitSupportMessage(category, message) {
        return new Promise((resolve, reject) => {
            const SubmitSupportMessageRequest = protoModule.SubmitSupportMessageRequest;
            const categoryEnum = category === 'feature'
                ? protoModule.SupportMessageCategory.SUPPORT_MESSAGE_CATEGORY_FEATURE_REQUEST
                : protoModule.SupportMessageCategory.SUPPORT_MESSAGE_CATEGORY_SUPPORT;
            const request = new SubmitSupportMessageRequest();
            request.setCategory(categoryEnum);
            request.setMessage(message);
            this.getAuthenticatedClient().submitSupportMessage(request, {}, (err, response) => {
                if (err) {
                    console.error('Submit support message error:', err);
                    reject(new Error(`Request failed: ${err.code} ${err.message}`));
                    return;
                }
                resolve();
            });
        });
    }
    /**
     * Records the answers to the step every new account sees before the tour.
     * usagePurpose and teamSize are proto enum values; otherDescription is
     * required when the chosen option requires a description. The consent fields
     * are only read by the server when GetCurrentUser said needsConsentChoice.
     */
    async setUsagePurpose(usagePurpose, otherDescription, teamSize, consent) {
        return new Promise((resolve, reject) => {
            const SetUsagePurposeRequest = protoModule.SetUsagePurposeRequest;
            const request = new SetUsagePurposeRequest();
            request.setUsagePurpose(usagePurpose);
            request.setOtherUsageDescription(otherDescription || '');
            request.setTeamSize(teamSize);
            request.setMarketingEmailConsent(consent.marketingEmail);
            request.setAdAudienceConsent(consent.adAudience);
            request.setConsentWordingVersions(buildConsentWordingVersions(protoModule));
            this.getAuthenticatedClient().setUsagePurpose(request, {}, (err, response) => {
                if (err) {
                    console.error('Set usage purpose error:', err);
                    reject(new Error(`Request failed: ${err.code} ${err.message}`));
                    return;
                }
                resolve();
            });
        });
    }
    /**
     * The groups, options and team sizes the work and team step renders, in
     * display order, from the server-side registry.
     */
    async listUsagePurposeOptions() {
        return new Promise((resolve, reject) => {
            const request = new protoModule.ListUsagePurposeOptionsRequest();
            this.getAuthenticatedClient().listUsagePurposeOptions(request, {}, (err, response) => {
                if (err) {
                    console.error('List usage purpose options error:', err);
                    reject(new Error(`Request failed: ${err.code} ${err.message}`));
                    return;
                }
                if (!response) {
                    reject(new Error('No response received'));
                    return;
                }
                resolve({
                    groups: response.getGroupsList().map((group) => ({
                        key: group.getKey(),
                        label: group.getLabel(),
                        options: group.getOptionsList().map((option) => ({
                            value: option.getValue(),
                            label: option.getLabel(),
                            detail: option.getDetail(),
                            avatar: option.getAvatar(),
                            requiresDescription: option.getRequiresDescription(),
                        })),
                    })),
                    teamSizes: response.getTeamSizesList().map((size) => ({
                        value: size.getValue(),
                        label: size.getLabel(),
                    })),
                });
            });
        });
    }
    /**
     * Reads the caller's communication consent for the Settings tab.
     */
    async getMyCommunicationPreferences() {
        return new Promise((resolve, reject) => {
            const request = new protoModule.GetMyCommunicationPreferencesRequest();
            this.getAuthenticatedClient().getMyCommunicationPreferences(request, {}, (err, response) => {
                if (err) {
                    console.error('Get communication preferences error:', err);
                    reject(new Error(`Request failed: ${err.code} ${err.message}`));
                    return;
                }
                const preferences = response.getPreferences();
                if (!preferences) {
                    reject(new Error('Communication preferences response carried no preferences'));
                    return;
                }
                resolve({ marketingEmail: preferences.getMarketingEmail(), adAudience: preferences.getAdAudience() });
            });
        });
    }
    /**
     * Saves the caller's communication consent from Settings (full desired state),
     * naming the wording versions shown. Returns the stored state.
     */
    async updateMyCommunicationPreferences(choice) {
        return new Promise((resolve, reject) => {
            const request = new protoModule.UpdateMyCommunicationPreferencesRequest();
            request.setMarketingEmail(choice.marketingEmail);
            request.setAdAudience(choice.adAudience);
            request.setConsentWordingVersions(buildConsentWordingVersions(protoModule));
            this.getAuthenticatedClient().updateMyCommunicationPreferences(request, {}, (err, response) => {
                if (err) {
                    console.error('Update communication preferences error:', err);
                    reject(new Error(`Request failed: ${err.code} ${err.message}`));
                    return;
                }
                const preferences = response.getPreferences();
                if (!preferences) {
                    reject(new Error('Communication preferences response carried no preferences'));
                    return;
                }
                resolve({ marketingEmail: preferences.getMarketingEmail(), adAudience: preferences.getAdAudience() });
            });
        });
    }
    /**
     * Asks for the caller's account to be deleted. Nothing is deleted: the team
     * is notified and a person runs the deletion. A repeat request returns the
     * original time with alreadyRequested set.
     */
    async requestAccountDeletion(reason) {
        return new Promise((resolve, reject) => {
            const request = new protoModule.RequestAccountDeletionRequest();
            request.setReason(reason);
            this.getAuthenticatedClient().requestAccountDeletion(request, {}, (err, response) => {
                if (err) {
                    console.error('Request account deletion error:', err);
                    reject(new Error(`Request failed: ${err.code} ${err.message}`));
                    return;
                }
                resolve({ requestedAt: response.getRequestedAt(), alreadyRequested: response.getAlreadyRequested() });
            });
        });
    }
    /**
     * Upload a profile photo (avatar). Image bytes should already be resized to a
     * small square thumbnail. Returns the stored photo's URL.
     */
    async uploadAvatar(image, contentType) {
        return new Promise((resolve, reject) => {
            const UploadAvatarRequest = protoModule.UploadAvatarRequest;
            const request = new UploadAvatarRequest();
            request.setImage(image);
            request.setContentType(contentType);
            this.getAuthenticatedClient().uploadAvatar(request, {}, (err, response) => {
                if (err) {
                    console.error('Upload avatar error:', err);
                    reject(new Error(`Request failed: ${err.code} ${err.message}`));
                    return;
                }
                if (response) {
                    resolve({ avatarUrl: response.getAvatarUrl() || '' });
                }
                else {
                    reject(new Error('No response received'));
                }
            });
        });
    }
    /**
     * Remove the stored profile photo, reverting to the colored-initials avatar.
     */
    async removeAvatar() {
        return new Promise((resolve, reject) => {
            const RemoveAvatarRequest = protoModule.RemoveAvatarRequest;
            const request = new RemoveAvatarRequest();
            this.getAuthenticatedClient().removeAvatar(request, {}, (err, response) => {
                if (err) {
                    console.error('Remove avatar error:', err);
                    reject(new Error(`Request failed: ${err.code} ${err.message}`));
                    return;
                }
                resolve();
            });
        });
    }
    /**
     * Request password reset by email (unauthenticated)
     */
    async requestPasswordResetByEmail(email) {
        return new Promise((resolve, reject) => {
            const RequestPasswordResetByEmailRequest = protoModule.RequestPasswordResetByEmailRequest;
            const request = new RequestPasswordResetByEmailRequest();
            request.setEmail(email);
            this.userClient.requestPasswordReset(request, {}, (err, response) => {
                if (err) {
                    console.error('Request password reset by email error:', err);
                    reject(new Error(`Request failed: ${err.code} ${err.message}`));
                    return;
                }
                if (response) {
                    const success = response.getSuccess();
                    const message = response.getMessage();
                    resolve({ success, message: message || 'Reset request processed' });
                }
                else {
                    reject(new Error('No response received'));
                }
            });
        });
    }
    /**
     * Reset password with reset code
     */
    async resetPassword(credentials) {
        return new Promise((resolve, reject) => {
            const ResetPasswordRequest = protoModule.ResetPasswordRequest;
            const request = new ResetPasswordRequest();
            request.setUserId(credentials.userId);
            request.setResetCode(credentials.resetCode);
            request.setNewPassword(credentials.newPassword);
            request.setCsrfToken(credentials.csrfToken);
            this.userClient.resetPassword(request, {}, (err, response) => {
                if (err) {
                    console.error('Reset password error:', err);
                    reject(new Error(`Reset failed: ${err.code} ${err.message}`));
                    return;
                }
                if (response) {
                    const success = response.getSuccess();
                    const message = response.getMessage();
                    if (success) {
                        const sessionId = response.getSessionId();
                        const sessionToken = response.getSessionToken();
                        if (sessionId && sessionToken) {
                            // Store tokens to automatically log user in
                            localStorage.setItem(this.SESSION_ID_KEY, sessionId);
                            localStorage.setItem(this.SESSION_TOKEN_KEY, sessionToken);
                            resolve({ success: true, message: message || 'Password reset successful' });
                        }
                        else {
                            resolve({ success: false, message: 'Password reset succeeded but no login session created' });
                        }
                    }
                    else {
                        resolve({ success: false, message: message || 'Password reset failed' });
                    }
                }
                else {
                    reject(new Error('No response received'));
                }
            });
        });
    }
    /**
     * Resend verification email (unauthenticated)
     */
    async resendVerificationEmail(email) {
        return new Promise((resolve, reject) => {
            const ResendVerificationEmailRequest = protoModule.ResendVerificationEmailRequest;
            const request = new ResendVerificationEmailRequest();
            request.setEmail(email);
            this.userClient.resendVerificationEmail(request, {}, (err, response) => {
                if (err) {
                    console.error('Resend verification email error:', err);
                    reject(new Error(`Request failed: ${err.code} ${err.message}`));
                    return;
                }
                if (response) {
                    const success = response.getSuccess();
                    const message = response.getMessage();
                    resolve({ success, message: message || 'Verification email sent' });
                }
                else {
                    reject(new Error('No response received'));
                }
            });
        });
    }
}
// Export singleton instance
export const authService = new AuthService();
